import { createRoot } from "react-dom/client";
// Inter latin 400+600 ora caricati via @font-face in index.css con preload chirurgico
// in index.html (vedi link rel=preload). Salta cascata CSS @import → fetch font.
import App from "./App.tsx";
import "./index.css";
import { CONSENT_EVENT, isAllowed } from "./lib/consent";

// Sentry gating (B3 fix): telemetry must not start without explicit analytics consent (nLPD art.19).
// Audit V4 fix: detach listener al primo init (no leak); traces=0 in dev (no quota burn).
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
const isDev = import.meta.env.MODE !== "production";
let sentryInitialized = false;
let consentListener: (() => void) | null = null;
async function initSentryIfAllowed() {
  if (sentryInitialized || !sentryDsn) return;
  if (!isAllowed("analytics")) return;
  sentryInitialized = true;
  if (consentListener) {
    window.removeEventListener(CONSENT_EVENT, consentListener);
    consentListener = null;
  }
  const Sentry = await import("@sentry/react");
  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.MODE,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: isDev ? 0 : 0.05,
    tracePropagationTargets: [/^https:\/\/swissez\.ch/, /^https:\/\/.*\.supabase\.co/],
    profilesSampleRate: isDev ? 0 : 0.01,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: isDev ? 0 : 0.05,
    sendDefaultPii: false,
  });
}
void initSentryIfAllowed();
if (!sentryInitialized) {
  consentListener = () => { void initSentryIfAllowed(); };
  window.addEventListener(CONSENT_EVENT, consentListener);
}

const loadLater = () => {
  import("@fontsource/inter/500.css");
  import("@fontsource/inter/700.css");
  // Instrument Serif: ora @font-face inline in index.css con font-display:optional.
  // Drop @fontsource imports per evitare double-load + override swap default.
};
type IdleCB = (cb: () => void, opts?: { timeout?: number }) => number
const ric = (window as unknown as { requestIdleCallback?: IdleCB }).requestIdleCallback
if (ric) {
  ric(loadLater, { timeout: 3000 });
} else {
  setTimeout(loadLater, 1500);
}

function hideLoader() {
  const loader = document.getElementById("app-loader");
  if (loader) {
    loader.classList.add("hide");
    setTimeout(() => loader.remove(), 500);
  }
}

const rootElement = document.getElementById("root")!;
createRoot(rootElement).render(<App />);

// Safety net: 3s fallback se MutationObserver non scatta (3G safety pass W).
const fallbackId = setTimeout(hideLoader, 3000);

// Hide loader appena #root ha contenuto reale (post-hydration prerender SSG
// vite-prerender-plugin OR post-React mount). Più affidabile di rAF.
const observer = new MutationObserver(() => {
  if (rootElement.children.length > 0) {
    clearTimeout(fallbackId);
    hideLoader();
    observer.disconnect();
  }
});
observer.observe(rootElement, { childList: true });
